var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { AnimationEngine } from '../animation/Animation';
import { colorWithAlpha } from '../utils/color';
import { getNodeBoxInParent } from './coords';
import { findEdgeLayer, findNodeByDiagramId, nodeDiagramId } from './editor/collect';
import { getDiagramState, setDiagramState } from './helpers';
import { getActiveDiagram } from './theme';
import { ensureCanNetworkFlowEdges } from './canFlow';
import { syncDiagramToolbar, uninstallDiagramToolbar } from './toolbar';
function readStatusSnapshot(root) {
    var _a;
    var snap = (_a = root.metadata) === null || _a === void 0 ? void 0 : _a[FLOW_STATUS_SNAP_KEY];
    if (!snap || typeof snap !== 'object')
        return undefined;
    if (!snap.nodes || typeof snap.nodes !== 'object')
        return undefined;
    return {
        nodes: __assign({}, snap.nodes),
        edges: snap.edges && typeof snap.edges === 'object' ? __assign({}, snap.edges) : {},
    };
}
function writeStatusSnapshot(root, nodes, edges) {
    if (edges === void 0) { edges = {}; }
    root.metadata[FLOW_STATUS_SNAP_KEY] = { nodes: __assign({}, nodes), edges: __assign({}, edges) };
}
function clearStatusSnapshot(root) {
    delete root.metadata[FLOW_STATUS_SNAP_KEY];
}
function mapFromRecord(rec) {
    return new Map(Object.entries(rec));
}
var FLOW_OVERLAY_KEY = 'diagramFlowOverlay';
var FLOW_PACKET_KEY = 'diagramFlowPacket';
var FLOW_RUNTIME_KEY = 'diagramFlowRuntime';
var FLOW_STATUS_KEY = 'flowStatusChrome';
/** Last painted node/edge status — kept across soft pause. */
var FLOW_STATUS_SNAP_KEY = 'diagramFlowStatusSnapshot';
var DEFAULT_DASH = [10, 8];
var BASE_DASH_MS = 1600;
var BASE_PACKET_MS = 2400;
var BASE_PULSE_MS = 1100;
var FLASH_MS = 420;
var DEFAULT_PATH_GAP_MS = 450;
/** Default path-status tint palette. */
export var DEFAULT_FLOW_STATUS_COLORS = {
    idle: '#94a3b8',
    active: '#eab308',
    done: '#22c55e',
    error: '#ef4444',
};
export function resolveFlowStatusColors(raw) {
    return {
        idle: typeof (raw === null || raw === void 0 ? void 0 : raw.idle) === 'string' && raw.idle ? raw.idle : DEFAULT_FLOW_STATUS_COLORS.idle,
        active: typeof (raw === null || raw === void 0 ? void 0 : raw.active) === 'string' && raw.active
            ? raw.active
            : DEFAULT_FLOW_STATUS_COLORS.active,
        done: typeof (raw === null || raw === void 0 ? void 0 : raw.done) === 'string' && raw.done ? raw.done : DEFAULT_FLOW_STATUS_COLORS.done,
        error: typeof (raw === null || raw === void 0 ? void 0 : raw.error) === 'string' && raw.error ? raw.error : DEFAULT_FLOW_STATUS_COLORS.error,
    };
}
/** Ordered unique node ids from a hop list (stable first-seen order). */
export function nodeIdsFromHops(hops) {
    var e_1, _a;
    var ids = [];
    try {
        for (var hops_1 = __values(hops), hops_1_1 = hops_1.next(); !hops_1_1.done; hops_1_1 = hops_1.next()) {
            var h = hops_1_1.value;
            if (!ids.includes(h.from))
                ids.push(h.from);
            if (!ids.includes(h.to))
                ids.push(h.to);
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (hops_1_1 && !hops_1_1.done && (_a = hops_1.return)) _a.call(hops_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
    return ids;
}
/** Stable edge key for status maps (`from->to`). */
export function flowEdgeKey(from, to) {
    return "".concat(from, "->").concat(to);
}
export function isDiagramFlowNodeStatus(v) {
    return v === 'idle' || v === 'active' || v === 'done' || v === 'error';
}
export function sanitizeStatusOverrides(raw) {
    var e_2, _a;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw))
        return undefined;
    var out = {};
    try {
        for (var _b = __values(Object.entries(raw)), _c = _b.next(); !_c.done; _c = _b.next()) {
            var _d = __read(_c.value, 2), k = _d[0], v = _d[1];
            if (typeof k === 'string' && k && isDiagramFlowNodeStatus(v))
                out[k] = v;
        }
    }
    catch (e_2_1) { e_2 = { error: e_2_1 }; }
    finally {
        try {
            if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
        }
        finally { if (e_2) throw e_2.error; }
    }
    return Object.keys(out).length > 0 ? out : undefined;
}
/**
 * Pure idle → active → done transitions for a path run (no canvas).
 * Used by the runtime and unit tests.
 */
export function createFlowStatusMap() {
    var states = new Map();
    var edgeStates = new Map();
    return {
        states: states,
        edgeStates: edgeStates,
        beginRun: function (ids, edgeKeys) {
            var e_3, _a, e_4, _b;
            if (edgeKeys === void 0) { edgeKeys = []; }
            states.clear();
            edgeStates.clear();
            try {
                for (var ids_1 = __values(ids), ids_1_1 = ids_1.next(); !ids_1_1.done; ids_1_1 = ids_1.next()) {
                    var id = ids_1_1.value;
                    states.set(id, 'idle');
                }
            }
            catch (e_3_1) { e_3 = { error: e_3_1 }; }
            finally {
                try {
                    if (ids_1_1 && !ids_1_1.done && (_a = ids_1.return)) _a.call(ids_1);
                }
                finally { if (e_3) throw e_3.error; }
            }
            if (ids.length > 0)
                states.set(ids[0], 'active');
            try {
                for (var edgeKeys_1 = __values(edgeKeys), edgeKeys_1_1 = edgeKeys_1.next(); !edgeKeys_1_1.done; edgeKeys_1_1 = edgeKeys_1.next()) {
                    var key = edgeKeys_1_1.value;
                    edgeStates.set(key, 'idle');
                }
            }
            catch (e_4_1) { e_4 = { error: e_4_1 }; }
            finally {
                try {
                    if (edgeKeys_1_1 && !edgeKeys_1_1.done && (_b = edgeKeys_1.return)) _b.call(edgeKeys_1);
                }
                finally { if (e_4) throw e_4.error; }
            }
        },
        hopStart: function (from, to) {
            states.set(from, 'active');
            edgeStates.set(flowEdgeKey(from, to), 'active');
        },
        hopArrive: function (from, to, isLast) {
            states.set(from, 'done');
            states.set(to, isLast ? 'done' : 'active');
            edgeStates.set(flowEdgeKey(from, to), 'done');
        },
        setError: function (nodeId) {
            states.set(nodeId, 'error');
        },
        applyOverrides: function (overrides) {
            var e_5, _a;
            if (!overrides)
                return;
            try {
                for (var _b = __values(Object.entries(overrides)), _c = _b.next(); !_c.done; _c = _b.next()) {
                    var _d = __read(_c.value, 2), id = _d[0], status_1 = _d[1];
                    states.set(id, status_1);
                }
            }
            catch (e_5_1) { e_5 = { error: e_5_1 }; }
            finally {
                try {
                    if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
                }
                finally { if (e_5) throw e_5.error; }
            }
        },
        reset: function () {
            states.clear();
            edgeStates.clear();
        },
        snapshot: function () {
            var e_6, _a;
            var out = {};
            try {
                for (var states_1 = __values(states), states_1_1 = states_1.next(); !states_1_1.done; states_1_1 = states_1.next()) {
                    var _b = __read(states_1_1.value, 2), k = _b[0], v = _b[1];
                    out[k] = v;
                }
            }
            catch (e_6_1) { e_6 = { error: e_6_1 }; }
            finally {
                try {
                    if (states_1_1 && !states_1_1.done && (_a = states_1.return)) _a.call(states_1);
                }
                finally { if (e_6) throw e_6.error; }
            }
            return out;
        },
        edgeSnapshot: function () {
            var e_7, _a;
            var out = {};
            try {
                for (var edgeStates_1 = __values(edgeStates), edgeStates_1_1 = edgeStates_1.next(); !edgeStates_1_1.done; edgeStates_1_1 = edgeStates_1.next()) {
                    var _b = __read(edgeStates_1_1.value, 2), k = _b[0], v = _b[1];
                    out[k] = v;
                }
            }
            catch (e_7_1) { e_7 = { error: e_7_1 }; }
            finally {
                try {
                    if (edgeStates_1_1 && !edgeStates_1_1.done && (_a = edgeStates_1.return)) _a.call(edgeStates_1);
                }
                finally { if (e_7) throw e_7.error; }
            }
            return out;
        },
    };
}
function nodeListToHops(nodes) {
    var hops = [];
    for (var i = 0; i < nodes.length - 1; i++) {
        hops.push({ from: String(nodes[i]), to: String(nodes[i + 1]) });
    }
    return hops;
}
function isStringArray(v) {
    return Array.isArray(v) && v.length > 0 && typeof v[0] === 'string';
}
function isPathsMatrix(v) {
    return Array.isArray(v) && v.length > 0 && Array.isArray(v[0]);
}
function sanitizeHopList(list) {
    if (!Array.isArray(list))
        return [];
    return list
        .filter(function (h) {
        return !!h && typeof h.from === 'string' && typeof h.to === 'string';
    })
        .map(function (h) { return ({ from: h.from, to: h.to }); });
}
function normalizeFlow(raw) {
    var _a, _b, _c;
    var mode = (_a = raw === null || raw === void 0 ? void 0 : raw.mode) !== null && _a !== void 0 ? _a : 'both';
    var highlight = (_b = raw === null || raw === void 0 ? void 0 : raw.highlight) !== null && _b !== void 0 ? _b : 'pulse';
    var playback = (raw === null || raw === void 0 ? void 0 : raw.playback) === 'once' ? 'once' : 'loop';
    var speed = typeof (raw === null || raw === void 0 ? void 0 : raw.speed) === 'number' && Number.isFinite(raw.speed) ? Math.max(0, raw.speed) : 1;
    var paused = (raw === null || raw === void 0 ? void 0 : raw.paused) === true || speed <= 0;
    var pathGapMs = typeof (raw === null || raw === void 0 ? void 0 : raw.pathGapMs) === 'number' && Number.isFinite(raw.pathGapMs)
        ? Math.max(0, raw.pathGapMs)
        : DEFAULT_PATH_GAP_MS;
    var pathRuns = [];
    var paths;
    var path;
    if (Array.isArray(raw === null || raw === void 0 ? void 0 : raw.pathsEdges) && raw.pathsEdges.length > 0) {
        pathRuns = raw.pathsEdges.map(sanitizeHopList).filter(function (r) { return r.length > 0; });
    }
    else if (isPathsMatrix(raw === null || raw === void 0 ? void 0 : raw.paths)) {
        paths = raw.paths.map(function (p) { return p.map(String); }).filter(function (p) { return p.length >= 2; });
        pathRuns = paths.map(nodeListToHops).filter(function (r) { return r.length > 0; });
    }
    else if (isPathsMatrix(raw === null || raw === void 0 ? void 0 : raw.path)) {
        // Allow path: string[][] as shorthand for paths
        paths = raw.path.map(function (p) { return p.map(String); }).filter(function (p) { return p.length >= 2; });
        pathRuns = paths.map(nodeListToHops).filter(function (r) { return r.length > 0; });
    }
    else {
        var explicit = sanitizeHopList((_c = raw === null || raw === void 0 ? void 0 : raw.pathEdges) !== null && _c !== void 0 ? _c : raw === null || raw === void 0 ? void 0 : raw.edges);
        if (explicit.length > 0) {
            pathRuns = [explicit];
        }
        else if (isStringArray(raw === null || raw === void 0 ? void 0 : raw.path) && raw.path.length >= 2) {
            path = raw.path.map(String);
            pathRuns = [nodeListToHops(path)];
            paths = [path];
        }
    }
    var pathEdges = pathRuns.length > 0 ? pathRuns.flat() : undefined;
    var statusHighlight = (raw === null || raw === void 0 ? void 0 : raw.statusHighlight) !== undefined ? raw.statusHighlight === true : pathRuns.length > 0;
    var statusEdges = (raw === null || raw === void 0 ? void 0 : raw.statusEdges) !== undefined ? raw.statusEdges === true : statusHighlight;
    var statusColors = resolveFlowStatusColors(raw === null || raw === void 0 ? void 0 : raw.statusColors);
    var statusOverrides = sanitizeStatusOverrides(raw === null || raw === void 0 ? void 0 : raw.statusOverrides);
    var statusPauseOnError = (raw === null || raw === void 0 ? void 0 : raw.statusPauseOnError) !== false;
    return {
        enabled: (raw === null || raw === void 0 ? void 0 : raw.enabled) !== false,
        speed: paused && speed <= 0 ? 0 : speed,
        paused: paused,
        playback: playback,
        mode: mode === 'dash' || mode === 'packet' || mode === 'both' ? mode : 'both',
        highlight: highlight === 'pulse' || highlight === 'breathe' || highlight === 'flash' || highlight === 'none'
            ? highlight
            : 'pulse',
        statusHighlight: statusHighlight,
        statusEdges: statusEdges,
        statusColors: statusColors,
        statusOverrides: statusOverrides,
        statusPauseOnError: statusPauseOnError,
        activeNodes: raw === null || raw === void 0 ? void 0 : raw.activeNodes,
        activeEdges: raw === null || raw === void 0 ? void 0 : raw.activeEdges,
        path: path,
        paths: paths,
        pathEdges: pathEdges,
        pathsEdges: Array.isArray(raw === null || raw === void 0 ? void 0 : raw.pathsEdges) ? pathRuns : undefined,
        pathRuns: pathRuns,
        pathGapMs: pathGapMs,
        dashPattern: Array.isArray(raw === null || raw === void 0 ? void 0 : raw.dashPattern) && raw.dashPattern.length >= 2
            ? raw.dashPattern.map(Number)
            : DEFAULT_DASH,
        chrome: raw === null || raw === void 0 ? void 0 : raw.chrome,
    };
}
/** Flat polyline points → SVG path `d` for motionPath. */
export function edgePointsToPathD(points) {
    if (!Array.isArray(points) || points.length < 4)
        return '';
    var d = "M ".concat(points[0], " ").concat(points[1]);
    for (var i = 2; i < points.length; i += 2) {
        var x = points[i];
        var y = points[i + 1];
        if (!Number.isFinite(x) || !Number.isFinite(y))
            continue;
        d += " L ".concat(x, " ").concat(y);
    }
    return d;
}
/** Visible stroke polyline inside an edge group (not hit-target / packet). */
export function getEdgeStrokePolyline(edge) {
    var tagged = edge.children.find(function (c) { var _a; return c.type === 'polyline' && ((_a = c.metadata) === null || _a === void 0 ? void 0 : _a.edgeStrokePoly) === true; });
    if (tagged)
        return tagged;
    var polylines = edge.children.filter(function (c) {
        var _a, _b;
        return c.type === 'polyline' &&
            !((_a = c.metadata) === null || _a === void 0 ? void 0 : _a.edgeHitPolyline) &&
            !((_b = c.metadata) === null || _b === void 0 ? void 0 : _b[FLOW_PACKET_KEY]);
    });
    if (polylines.length === 0)
        return undefined;
    return polylines.length >= 2 ? polylines[1] : polylines[0];
}
function dashPeriod(pattern) {
    return pattern.reduce(function (sum, n) { return sum + (Number.isFinite(n) ? Math.abs(n) : 0); }, 0) || 18;
}
function durationMs(base, speed) {
    if (speed <= 0)
        return 0;
    return Math.max(120, Math.round(base / speed));
}
function removeFlowChrome(root) {
    var e_8, _a, e_9, _b, e_10, _c;
    var _d, _e;
    try {
        for (var _f = __values(__spreadArray([], __read(root.children), false)), _g = _f.next(); !_g.done; _g = _f.next()) {
            var child = _g.value;
            if ((_d = child.metadata) === null || _d === void 0 ? void 0 : _d[FLOW_OVERLAY_KEY]) {
                root.remove(child);
                child.destroy();
            }
        }
    }
    catch (e_8_1) { e_8 = { error: e_8_1 }; }
    finally {
        try {
            if (_g && !_g.done && (_a = _f.return)) _a.call(_f);
        }
        finally { if (e_8) throw e_8.error; }
    }
    var edgeLayer = findEdgeLayer(root);
    if (!edgeLayer)
        return;
    try {
        for (var _h = __values(edgeLayer.children), _j = _h.next(); !_j.done; _j = _h.next()) {
            var edge = _j.value;
            try {
                for (var _k = (e_10 = void 0, __values(__spreadArray([], __read(edge.children), false))), _l = _k.next(); !_l.done; _l = _k.next()) {
                    var child = _l.value;
                    if ((_e = child.metadata) === null || _e === void 0 ? void 0 : _e[FLOW_PACKET_KEY]) {
                        edge.remove(child);
                        child.destroy();
                    }
                }
            }
            catch (e_10_1) { e_10 = { error: e_10_1 }; }
            finally {
                try {
                    if (_l && !_l.done && (_c = _k.return)) _c.call(_k);
                }
                finally { if (e_10) throw e_10.error; }
            }
        }
    }
    catch (e_9_1) { e_9 = { error: e_9_1 }; }
    finally {
        try {
            if (_j && !_j.done && (_b = _h.return)) _b.call(_h);
        }
        finally { if (e_9) throw e_9.error; }
    }
}
function clearPulseOnly(overlay) {
    var e_11, _a;
    var _b;
    try {
        for (var _c = __values(__spreadArray([], __read(overlay.children), false)), _d = _c.next(); !_d.done; _d = _c.next()) {
            var child = _d.value;
            if ((_b = child.metadata) === null || _b === void 0 ? void 0 : _b.flowPulseRing) {
                overlay.remove(child);
                child.destroy();
            }
        }
    }
    catch (e_11_1) { e_11 = { error: e_11_1 }; }
    finally {
        try {
            if (_d && !_d.done && (_a = _c.return)) _a.call(_c);
        }
        finally { if (e_11) throw e_11.error; }
    }
}
function clearStatusOnly(overlay) {
    var e_12, _a;
    var _b;
    try {
        for (var _c = __values(__spreadArray([], __read(overlay.children), false)), _d = _c.next(); !_d.done; _d = _c.next()) {
            var child = _d.value;
            if ((_b = child.metadata) === null || _b === void 0 ? void 0 : _b[FLOW_STATUS_KEY]) {
                overlay.remove(child);
                child.destroy();
            }
        }
    }
    catch (e_12_1) { e_12 = { error: e_12_1 }; }
    finally {
        try {
            if (_d && !_d.done && (_a = _c.return)) _a.call(_c);
        }
        finally { if (e_12) throw e_12.error; }
    }
}
function addStatusChrome(app, root, overlay, nodeId, status, colors) {
    var _a;
    var _b;
    var node = findNodeByDiagramId(root, nodeId);
    if (!node)
        return;
    var box = getNodeBoxInParent(node, root);
    var color = colors[status];
    var alpha = status === 'active' ? 0.28 : status === 'done' ? 0.2 : status === 'error' ? 0.24 : 0.12;
    var fill = (_b = colorWithAlpha(color, alpha)) !== null && _b !== void 0 ? _b : color;
    var pad = status === 'active' ? 5 : 3;
    var ring = app.rect({
        x: box.x - pad,
        y: box.y - pad,
        width: box.width + pad * 2,
        height: box.height + pad * 2,
        fill: fill,
        stroke: color,
        strokeWidth: status === 'active' ? 2.5 : status === 'error' ? 2.25 : 1.75,
        cornerRadius: 10,
        opacity: 1,
        listening: false,
        metadata: (_a = {},
            _a[FLOW_STATUS_KEY] = true,
            _a.flowStatusNodeId = nodeId,
            _a.flowStatus = status,
            _a),
    });
    overlay.add(ring);
}
function syncStatusChrome(app, root, getOverlay, states, colors) {
    var e_13, _a;
    var overlay = getOverlay();
    clearStatusOnly(overlay);
    try {
        for (var states_2 = __values(states), states_2_1 = states_2.next(); !states_2_1.done; states_2_1 = states_2.next()) {
            var _b = __read(states_2_1.value, 2), id = _b[0], status_2 = _b[1];
            addStatusChrome(app, root, overlay, id, status_2, colors);
        }
    }
    catch (e_13_1) { e_13 = { error: e_13_1 }; }
    finally {
        try {
            if (states_2_1 && !states_2_1.done && (_a = states_2.return)) _a.call(states_2);
        }
        finally { if (e_13) throw e_13.error; }
    }
}
function restoreEdgeStatusTints(edgeLayer) {
    var e_14, _a;
    var _b, _c;
    try {
        for (var _d = __values(edgeLayer.children), _e = _d.next(); !_e.done; _e = _d.next()) {
            var edgeChild = _e.value;
            var edge = edgeChild;
            if (((_b = edge.metadata) === null || _b === void 0 ? void 0 : _b.flowStrokePrior) === undefined)
                continue;
            var stroke = getEdgeStrokePolyline(edge);
            if (stroke) {
                stroke.stroke = edge.metadata.flowStrokePrior;
                (_c = stroke.markDirty) === null || _c === void 0 ? void 0 : _c.call(stroke);
            }
            delete edge.metadata.flowStrokePrior;
            delete edge.metadata.flowEdgeStatus;
        }
    }
    catch (e_14_1) { e_14 = { error: e_14_1 }; }
    finally {
        try {
            if (_e && !_e.done && (_a = _d.return)) _a.call(_d);
        }
        finally { if (e_14) throw e_14.error; }
    }
}
function applyEdgeStatusTint(edge, status, colors) {
    var _a, _b;
    var stroke = getEdgeStrokePolyline(edge);
    if (!stroke)
        return;
    if (edge.metadata.flowStrokePrior === undefined) {
        edge.metadata.flowStrokePrior = (_a = stroke.stroke) !== null && _a !== void 0 ? _a : null;
    }
    stroke.stroke = colors[status];
    edge.metadata.flowEdgeStatus = status;
    (_b = stroke.markDirty) === null || _b === void 0 ? void 0 : _b.call(stroke);
}
function syncEdgeStatusTints(edgeLayer, edgeStates, colors) {
    var e_15, _a;
    try {
        for (var edgeStates_2 = __values(edgeStates), edgeStates_2_1 = edgeStates_2.next(); !edgeStates_2_1.done; edgeStates_2_1 = edgeStates_2.next()) {
            var _b = __read(edgeStates_2_1.value, 2), key = _b[0], status_3 = _b[1];
            var sep = key.indexOf('->');
            if (sep < 0)
                continue;
            var from = key.slice(0, sep);
            var to = key.slice(sep + 2);
            var edge = findEdgeByHop(edgeLayer, from, to);
            if (edge)
                applyEdgeStatusTint(edge, status_3, colors);
        }
    }
    catch (e_15_1) { e_15 = { error: e_15_1 }; }
    finally {
        try {
            if (edgeStates_2_1 && !edgeStates_2_1.done && (_a = edgeStates_2.return)) _a.call(edgeStates_2);
        }
        finally { if (e_15) throw e_15.error; }
    }
}
/** Stop running flow animations; keep `diagramState.flow` unless `clearState`. */
export function stopDiagramFlow(root, opts) {
    var e_16, _a, e_17, _b;
    var _c, _d, _e, _f, _g;
    if (opts === void 0) { opts = {}; }
    var rt = (_c = root.metadata) === null || _c === void 0 ? void 0 : _c[FLOW_RUNTIME_KEY];
    if ((_d = rt === null || rt === void 0 ? void 0 : rt.handles) === null || _d === void 0 ? void 0 : _d.length) {
        try {
            for (var _h = __values(rt.handles), _j = _h.next(); !_j.done; _j = _h.next()) {
                var h = _j.value;
                try {
                    h.stop();
                }
                catch (_k) {
                    /* ignore */
                }
            }
        }
        catch (e_16_1) { e_16 = { error: e_16_1 }; }
        finally {
            try {
                if (_j && !_j.done && (_a = _h.return)) _a.call(_h);
            }
            finally { if (e_16) throw e_16.error; }
        }
    }
    delete root.metadata[FLOW_RUNTIME_KEY];
    removeFlowChrome(root);
    var edgeLayer = findEdgeLayer(root);
    if (edgeLayer) {
        restoreEdgeStatusTints(edgeLayer);
        try {
            for (var _l = __values(edgeLayer.children), _m = _l.next(); !_m.done; _m = _l.next()) {
                var edge = _m.value;
                var stroke = getEdgeStrokePolyline(edge);
                if (stroke && typeof stroke.dashOffset === 'number') {
                    stroke.dashOffset = 0;
                    (_e = stroke.markDirty) === null || _e === void 0 ? void 0 : _e.call(stroke);
                }
                if (((_f = edge.metadata) === null || _f === void 0 ? void 0 : _f.flowDashApplied) && stroke) {
                    var prior = edge.metadata.edgeDash;
                    if (!prior) {
                        stroke.dash = [];
                    }
                    delete edge.metadata.flowDashApplied;
                    (_g = stroke.markDirty) === null || _g === void 0 ? void 0 : _g.call(stroke);
                }
            }
        }
        catch (e_17_1) { e_17 = { error: e_17_1 }; }
        finally {
            try {
                if (_m && !_m.done && (_b = _l.return)) _b.call(_l);
            }
            finally { if (e_17) throw e_17.error; }
        }
    }
    if (opts.clearState) {
        clearStatusSnapshot(root);
        var state = getDiagramState(root);
        if (state.flow) {
            var next = __assign({}, state);
            delete next.flow;
            root.metadata.diagramState = next;
        }
        uninstallDiagramToolbar(root);
    }
    root.markDirty();
}
function ensureOverlay(app, root) {
    var _a;
    var overlay = root.children.find(function (c) { var _a; return (_a = c.metadata) === null || _a === void 0 ? void 0 : _a[FLOW_OVERLAY_KEY]; });
    if (overlay) {
        overlay.clear();
        return overlay;
    }
    overlay = app.group({
        listening: false,
        zIndex: 50,
        metadata: (_a = {}, _a[FLOW_OVERLAY_KEY] = true, _a),
    });
    root.add(overlay);
    return overlay;
}
function addNodePulse(app, root, overlay, nodeId, highlight, speed, handles, opts) {
    var _a, _b, _c;
    if (opts === void 0) { opts = { loop: true }; }
    if (highlight === 'none' || highlight === 'flash')
        return;
    var node = findNodeByDiagramId(root, nodeId);
    if (!node)
        return;
    var box = getNodeBoxInParent(node, root);
    var pad = 5;
    var theme = getActiveDiagram();
    var stroke = (_c = (_b = (_a = theme.mindBranch) === null || _a === void 0 ? void 0 : _a.stroke) !== null && _b !== void 0 ? _b : theme.edge) !== null && _c !== void 0 ? _c : '#38bdf8';
    var ring = app.rect({
        x: box.x - pad,
        y: box.y - pad,
        width: box.width + pad * 2,
        height: box.height + pad * 2,
        fill: null,
        stroke: stroke,
        strokeWidth: 2,
        cornerRadius: 10,
        opacity: highlight === 'breathe' ? 0.35 : 0.85,
        listening: false,
        metadata: { flowPulseRing: true },
    });
    overlay.add(ring);
    var dur = durationMs(BASE_PULSE_MS, speed);
    if (dur <= 0)
        return;
    if (highlight === 'breathe') {
        handles.push(AnimationEngine.animate(ring, {
            opacity: 0.9,
            duration: dur,
            loop: opts.loop,
            reverse: opts.loop,
            easing: 'easeInOut',
        }));
    }
    else {
        ring.dash = [6, 5];
        ring.dashOffset = 0;
        handles.push(AnimationEngine.animate(ring, {
            strokeWidth: 3.25,
            opacity: 0.35,
            duration: dur,
            loop: opts.loop,
            reverse: opts.loop,
            easing: 'easeInOut',
        }));
        handles.push(AnimationEngine.animate(ring, {
            dashOffset: 22,
            duration: Math.round(dur * 1.4),
            loop: opts.loop,
            easing: 'linear',
        }));
    }
}
function flashNode(app, root, overlay, nodeId, handles) {
    var _a, _b, _c;
    var node = findNodeByDiagramId(root, nodeId);
    if (!node)
        return;
    var box = getNodeBoxInParent(node, root);
    var theme = getActiveDiagram();
    var stroke = (_c = (_b = (_a = theme.flowchartStart) === null || _a === void 0 ? void 0 : _a.accent) !== null && _b !== void 0 ? _b : theme.edge) !== null && _c !== void 0 ? _c : '#4ade80';
    var flash = app.rect({
        x: box.x - 7,
        y: box.y - 7,
        width: box.width + 14,
        height: box.height + 14,
        fill: null,
        stroke: stroke,
        strokeWidth: 2.5,
        cornerRadius: 12,
        opacity: 1,
        listening: false,
    });
    overlay.add(flash);
    var control = AnimationEngine.animate(flash, {
        opacity: 0,
        strokeWidth: 1,
        duration: FLASH_MS,
        easing: 'easeOut',
        onComplete: function () {
            if (flash.parent) {
                flash.parent.remove(flash);
                flash.destroy();
            }
        },
    });
    handles.push(control);
}
function findEdgeByHop(edgeLayer, from, to) {
    return edgeLayer.children.find(function (c) {
        var _a, _b;
        var f = (_a = c.metadata) === null || _a === void 0 ? void 0 : _a.edgeFrom;
        var t = (_b = c.metadata) === null || _b === void 0 ? void 0 : _b.edgeTo;
        return f === from && t === to;
    });
}
function edgeMatchesActive(edge, activeEdges) {
    var _a, _b, _c;
    if (!activeEdges || activeEdges.length === 0)
        return true;
    var id = (_a = edge.metadata) === null || _a === void 0 ? void 0 : _a.edgeId;
    var from = (_b = edge.metadata) === null || _b === void 0 ? void 0 : _b.edgeFrom;
    var to = (_c = edge.metadata) === null || _c === void 0 ? void 0 : _c.edgeTo;
    var key = from && to ? "".concat(from, "->").concat(to) : undefined;
    return ((id != null && activeEdges.includes(id)) ||
        (key != null && activeEdges.includes(key)) ||
        (from != null && activeEdges.includes(from)) ||
        (to != null && activeEdges.includes(to)));
}
function edgeInPath(edge, hops) {
    var _a, _b;
    if (!hops || hops.length === 0)
        return true;
    var from = (_a = edge.metadata) === null || _a === void 0 ? void 0 : _a.edgeFrom;
    var to = (_b = edge.metadata) === null || _b === void 0 ? void 0 : _b.edgeTo;
    if (!from || !to)
        return false;
    return hops.some(function (h) { return h.from === from && h.to === to; });
}
function startAmbientPacket(app, root, edge, pathD, speed, highlight, playback, handles, getOverlay, onFinished) {
    var _a;
    var _b, _c, _d, _e;
    var theme = getActiveDiagram();
    var fill = (_d = (_c = (_b = theme.flowchartProcess) === null || _b === void 0 ? void 0 : _b.accent) !== null && _c !== void 0 ? _c : theme.edge) !== null && _d !== void 0 ? _d : '#60a5fa';
    var packet = app.circle({
        x: 0,
        y: 0,
        radius: 4.5,
        fill: fill,
        stroke: '#0f172a',
        strokeWidth: 1,
        listening: false,
        metadata: (_a = {}, _a[FLOW_PACKET_KEY] = true, _a),
    });
    edge.add(packet);
    var toId = (_e = edge.metadata) === null || _e === void 0 ? void 0 : _e.edgeTo;
    var dur = durationMs(BASE_PACKET_MS, speed);
    if (dur <= 0)
        return;
    var stopped = false;
    var control = null;
    var run = function () {
        if (stopped)
            return;
        control = AnimationEngine.animate(packet, {
            motionPath: pathD,
            rotation: 0,
            duration: dur,
            easing: 'linear',
            onComplete: function () {
                if (stopped)
                    return;
                if (toId && highlight !== 'none') {
                    flashNode(app, root, getOverlay(), toId, handles);
                }
                if (playback === 'loop') {
                    run();
                }
                else {
                    onFinished === null || onFinished === void 0 ? void 0 : onFinished();
                }
            },
        });
    };
    handles.push({
        stop: function () {
            stopped = true;
            control === null || control === void 0 ? void 0 : control.stop();
        },
        pause: function () {
            var _a;
            if (stopped)
                return;
            (_a = control === null || control === void 0 ? void 0 : control.pause) === null || _a === void 0 ? void 0 : _a.call(control);
        },
        resume: function () {
            var _a;
            if (stopped)
                return;
            (_a = control === null || control === void 0 ? void 0 : control.resume) === null || _a === void 0 ? void 0 : _a.call(control);
        },
    });
    run();
}
/**
 * Sequential packet along one or more path runs (index order).
 * After each run completes, waits `pathGapMs` before the next.
 */
function startPathSequence(app, root, runs, speed, highlight, playback, pathGapMs, handles, getOverlay, onFinished, statusOpts) {
    var _a, _b, _c;
    var validRuns = runs.filter(function (r) { return r.length > 0; });
    if (validRuns.length === 0)
        return;
    var theme = getActiveDiagram();
    var fill = (_c = (_b = (_a = theme.flowchartProcess) === null || _a === void 0 ? void 0 : _a.accent) !== null && _b !== void 0 ? _b : theme.edge) !== null && _c !== void 0 ? _c : '#60a5fa';
    var dur = durationMs(BASE_PACKET_MS, speed);
    if (dur <= 0)
        return;
    var stopped = false;
    var runIndex = 0;
    var hopIndex = 0;
    var packet = null;
    var control = null;
    var gapTimer = null;
    var gapDeadline = null;
    var gapRemainingMs = null;
    var softPaused = false;
    var pulseHandles = [];
    var statusMap = statusOpts ? createFlowStatusMap() : null;
    var paintStatus = function () {
        if (!statusMap || !statusOpts)
            return;
        statusMap.applyOverrides(statusOpts.overrides);
        syncStatusChrome(app, root, getOverlay, statusMap.states, statusOpts.colors);
        if (statusOpts.edges) {
            syncEdgeStatusTints(statusOpts.edgeLayer, statusMap.edgeStates, statusOpts.colors);
        }
        writeStatusSnapshot(root, statusMap.snapshot(), statusMap.edgeSnapshot());
    };
    var beginStatusRun = function (hops) {
        if (!statusMap)
            return;
        if (statusOpts === null || statusOpts === void 0 ? void 0 : statusOpts.edges)
            restoreEdgeStatusTints(statusOpts.edgeLayer);
        var edgeKeys = hops.map(function (h) { return flowEdgeKey(h.from, h.to); });
        statusMap.beginRun(nodeIdsFromHops(hops), edgeKeys);
        paintStatus();
    };
    var stopPulseHandles = function () {
        var e_18, _a;
        try {
            for (var pulseHandles_1 = __values(pulseHandles), pulseHandles_1_1 = pulseHandles_1.next(); !pulseHandles_1_1.done; pulseHandles_1_1 = pulseHandles_1.next()) {
                var h = pulseHandles_1_1.value;
                try {
                    h.stop();
                }
                catch (_b) {
                    /* ignore */
                }
            }
        }
        catch (e_18_1) { e_18 = { error: e_18_1 }; }
        finally {
            try {
                if (pulseHandles_1_1 && !pulseHandles_1_1.done && (_a = pulseHandles_1.return)) _a.call(pulseHandles_1);
            }
            finally { if (e_18) throw e_18.error; }
        }
        pulseHandles.length = 0;
    };
    var pausePulseHandles = function () {
        var e_19, _a;
        var _b;
        try {
            for (var pulseHandles_2 = __values(pulseHandles), pulseHandles_2_1 = pulseHandles_2.next(); !pulseHandles_2_1.done; pulseHandles_2_1 = pulseHandles_2.next()) {
                var h = pulseHandles_2_1.value;
                try {
                    (_b = h.pause) === null || _b === void 0 ? void 0 : _b.call(h);
                }
                catch (_c) {
                    /* ignore */
                }
            }
        }
        catch (e_19_1) { e_19 = { error: e_19_1 }; }
        finally {
            try {
                if (pulseHandles_2_1 && !pulseHandles_2_1.done && (_a = pulseHandles_2.return)) _a.call(pulseHandles_2);
            }
            finally { if (e_19) throw e_19.error; }
        }
    };
    var resumePulseHandles = function () {
        var e_20, _a;
        var _b;
        try {
            for (var pulseHandles_3 = __values(pulseHandles), pulseHandles_3_1 = pulseHandles_3.next(); !pulseHandles_3_1.done; pulseHandles_3_1 = pulseHandles_3.next()) {
                var h = pulseHandles_3_1.value;
                try {
                    (_b = h.resume) === null || _b === void 0 ? void 0 : _b.call(h);
                }
                catch (_c) {
                    /* ignore */
                }
            }
        }
        catch (e_20_1) { e_20 = { error: e_20_1 }; }
        finally {
            try {
                if (pulseHandles_3_1 && !pulseHandles_3_1.done && (_a = pulseHandles_3.return)) _a.call(pulseHandles_3);
            }
            finally { if (e_20) throw e_20.error; }
        }
    };
    var placePacket = function (edge) {
        var _a;
        if (packet === null || packet === void 0 ? void 0 : packet.parent) {
            packet.parent.remove(packet);
            packet.destroy();
        }
        packet = app.circle({
            x: 0,
            y: 0,
            radius: 4.5,
            fill: fill,
            stroke: '#0f172a',
            strokeWidth: 1,
            listening: false,
            metadata: (_a = {}, _a[FLOW_PACKET_KEY] = true, _a),
        });
        edge.add(packet);
        return packet;
    };
    var showHopHighlight = function (fromId, toId) {
        if (highlight === 'none' || highlight === 'flash')
            return;
        var overlay = getOverlay();
        stopPulseHandles();
        clearPulseOnly(overlay);
        addNodePulse(app, root, overlay, fromId, highlight, speed, pulseHandles, { loop: true });
        addNodePulse(app, root, overlay, toId, highlight, speed, pulseHandles, { loop: true });
    };
    var advanceAfterRun = function () {
        if (stopped)
            return;
        runIndex += 1;
        hopIndex = 0;
        if (runIndex >= validRuns.length) {
            if (playback === 'loop') {
                runIndex = 0;
            }
            else {
                onFinished === null || onFinished === void 0 ? void 0 : onFinished();
                return;
            }
        }
        var go = function () {
            gapTimer = null;
            gapDeadline = null;
            gapRemainingMs = null;
            if (stopped || softPaused)
                return;
            var next = validRuns[runIndex];
            if (next)
                beginStatusRun(next);
            runHop();
        };
        if (pathGapMs > 0) {
            gapDeadline = Date.now() + pathGapMs;
            gapRemainingMs = pathGapMs;
            gapTimer = setTimeout(go, pathGapMs);
        }
        else {
            go();
        }
    };
    var runHop = function () {
        if (stopped || softPaused)
            return;
        var hops = validRuns[runIndex];
        if (!hops || hopIndex >= hops.length) {
            advanceAfterRun();
            return;
        }
        var hop = hops[hopIndex];
        if (statusMap) {
            statusMap.hopStart(hop.from, hop.to);
            paintStatus();
        }
        showHopHighlight(hop.from, hop.to);
        var node = placePacket(hop.edge);
        control = AnimationEngine.animate(node, {
            motionPath: hop.pathD,
            rotation: 0,
            duration: dur,
            easing: 'linear',
            onComplete: function () {
                if (stopped || softPaused)
                    return;
                if (highlight !== 'none') {
                    flashNode(app, root, getOverlay(), hop.to, handles);
                }
                var isLast = hopIndex + 1 >= hops.length;
                if (statusMap) {
                    statusMap.hopArrive(hop.from, hop.to, isLast);
                    paintStatus();
                }
                hopIndex += 1;
                runHop();
            },
        });
    };
    handles.push({
        stop: function () {
            stopped = true;
            softPaused = false;
            control === null || control === void 0 ? void 0 : control.stop();
            stopPulseHandles();
            if (gapTimer != null) {
                clearTimeout(gapTimer);
                gapTimer = null;
            }
            gapDeadline = null;
            gapRemainingMs = null;
            if (packet === null || packet === void 0 ? void 0 : packet.parent) {
                packet.parent.remove(packet);
                packet.destroy();
                packet = null;
            }
        },
        pause: function () {
            var _a;
            if (stopped || softPaused)
                return;
            softPaused = true;
            (_a = control === null || control === void 0 ? void 0 : control.pause) === null || _a === void 0 ? void 0 : _a.call(control);
            pausePulseHandles();
            if (gapTimer != null && gapDeadline != null) {
                clearTimeout(gapTimer);
                gapTimer = null;
                gapRemainingMs = Math.max(0, gapDeadline - Date.now());
                gapDeadline = null;
            }
        },
        resume: function () {
            var _a;
            if (stopped || !softPaused)
                return;
            softPaused = false;
            (_a = control === null || control === void 0 ? void 0 : control.resume) === null || _a === void 0 ? void 0 : _a.call(control);
            resumePulseHandles();
            if (gapRemainingMs != null) {
                var wait = gapRemainingMs;
                gapRemainingMs = null;
                var go = function () {
                    gapTimer = null;
                    gapDeadline = null;
                    if (stopped || softPaused)
                        return;
                    var next = validRuns[runIndex];
                    if (next)
                        beginStatusRun(next);
                    runHop();
                };
                if (wait > 0) {
                    gapDeadline = Date.now() + wait;
                    gapRemainingMs = wait;
                    gapTimer = setTimeout(go, wait);
                }
                else {
                    go();
                }
            }
        },
    });
    var firstRun = validRuns[0];
    if (firstRun)
        beginStatusRun(firstRun);
    var first = firstRun === null || firstRun === void 0 ? void 0 : firstRun[0];
    if (highlight !== 'none' && highlight !== 'flash' && first) {
        showHopHighlight(first.from, first.to);
    }
    runHop();
}
function applyStaticDashes(edgeLayer, merged, hops) {
    var e_21, _a;
    var _b, _c;
    var pattern = merged.dashPattern;
    try {
        for (var _d = __values(edgeLayer.children), _e = _d.next(); !_e.done; _e = _d.next()) {
            var edgeChild = _e.value;
            var edge = edgeChild;
            if (!edgeInPath(edge, hops))
                continue;
            var stroke = getEdgeStrokePolyline(edge);
            if (!stroke)
                continue;
            var existing = (_c = (_b = edge.metadata) === null || _b === void 0 ? void 0 : _b.edgeDash) !== null && _c !== void 0 ? _c : (Array.isArray(stroke.dash) ? stroke.dash : undefined);
            if (!existing || existing.length < 2) {
                stroke.dash = pattern.slice();
                edge.metadata.flowDashApplied = true;
            }
        }
    }
    catch (e_21_1) { e_21 = { error: e_21_1 }; }
    finally {
        try {
            if (_e && !_e.done && (_a = _d.return)) _a.call(_d);
        }
        finally { if (e_21) throw e_21.error; }
    }
}
/**
 * Apply (or re-apply) flow animation on a diagram root.
 * Persists options under `diagramState.flow`.
 */
export function applyDiagramFlow(app, root, options) {
    var e_22, _a, e_23, _b, e_24, _c, e_25, _d, e_26, _e, e_27, _f, e_28, _g;
    var _h, _j, _k, _l, _m, _o, _p, _q, _r, _s;
    if (options === void 0) { options = {}; }
    var prev = (_h = getDiagramState(root).flow) !== null && _h !== void 0 ? _h : {};
    var merged = normalizeFlow(__assign(__assign({}, prev), options));
    // Persist without ephemeral runtime flags duplication
    setDiagramState(root, {
        flow: {
            enabled: merged.enabled,
            speed: merged.speed,
            paused: merged.paused,
            playback: merged.playback,
            mode: merged.mode,
            highlight: merged.highlight,
            statusHighlight: merged.statusHighlight,
            statusEdges: merged.statusEdges,
            statusColors: merged.statusColors,
            statusOverrides: merged.statusOverrides,
            statusPauseOnError: merged.statusPauseOnError,
            activeNodes: merged.activeNodes,
            activeEdges: merged.activeEdges,
            path: merged.path,
            paths: merged.paths,
            pathEdges: merged.pathEdges,
            pathsEdges: merged.pathsEdges,
            pathGapMs: merged.pathGapMs,
            dashPattern: merged.dashPattern,
            chrome: merged.chrome,
        },
    });
    stopDiagramFlow(root);
    // CAN: build virtual bus-rail edges so path/ambient packets have geometry
    if (((_j = root.metadata) === null || _j === void 0 ? void 0 : _j.diagramType) === 'canNetwork') {
        var hopList = merged.pathRuns.length > 0 ? merged.pathRuns.flat() : undefined;
        ensureCanNetworkFlowEdges(app, root, hopList);
    }
    var edgeLayer = findEdgeLayer(root);
    var hopsSpec = merged.pathEdges;
    var isPlaying = merged.enabled && !merged.paused && merged.speed > 0;
    var overlay = null;
    var getOverlay = function () {
        if (!overlay)
            overlay = ensureOverlay(app, root);
        return overlay;
    };
    var paintStaticStatus = function (states, edgeStates) {
        if (!merged.statusHighlight)
            return;
        syncStatusChrome(app, root, getOverlay, states, merged.statusColors);
        if (merged.statusEdges && edgeLayer && edgeStates) {
            syncEdgeStatusTints(edgeLayer, edgeStates, merged.statusColors);
        }
    };
    if (!merged.enabled) {
        clearStatusSnapshot(root);
        root.metadata[FLOW_RUNTIME_KEY] = { handles: [], options: merged, playing: false };
        root.markDirty();
        syncDiagramToolbar(app, root);
        return;
    }
    // Soft pause keeps mid-hop progress; hard pause (no runtime) still shows last status snapshot.
    if (!isPlaying) {
        if (edgeLayer && (merged.mode === 'dash' || merged.mode === 'both')) {
            applyStaticDashes(edgeLayer, merged, hopsSpec);
        }
        if (merged.statusHighlight) {
            var snap = readStatusSnapshot(root);
            if (snap) {
                paintStaticStatus(mapFromRecord(snap.nodes), mapFromRecord(snap.edges));
            }
            else if (merged.statusOverrides) {
                var map = createFlowStatusMap();
                map.applyOverrides(merged.statusOverrides);
                paintStaticStatus(map.states);
                writeStatusSnapshot(root, map.snapshot());
            }
        }
        root.metadata[FLOW_RUNTIME_KEY] = { handles: [], options: merged, playing: false };
        root.markDirty();
        (_k = app.requestRender) === null || _k === void 0 ? void 0 : _k.call(app);
        syncDiagramToolbar(app, root);
        return;
    }
    var handles = [];
    var useDash = merged.mode === 'dash' || merged.mode === 'both';
    var usePacket = merged.mode === 'packet' || merged.mode === 'both';
    var pattern = merged.dashPattern;
    var period = dashPeriod(pattern);
    var dashDur = durationMs(BASE_DASH_MS, merged.speed);
    var loopDash = merged.playback === 'loop';
    var pathRunsResolved = [];
    var missingHops = [];
    if (edgeLayer && merged.pathRuns.length > 0) {
        try {
            for (var _t = __values(merged.pathRuns), _u = _t.next(); !_u.done; _u = _t.next()) {
                var run = _u.value;
                var resolved = [];
                try {
                    for (var run_1 = (e_23 = void 0, __values(run)), run_1_1 = run_1.next(); !run_1_1.done; run_1_1 = run_1.next()) {
                        var hop = run_1_1.value;
                        var edge = findEdgeByHop(edgeLayer, hop.from, hop.to);
                        if (!edge) {
                            missingHops.push(hop);
                            continue;
                        }
                        var points = (_l = edge.metadata) === null || _l === void 0 ? void 0 : _l.edgePoints;
                        var d = edgePointsToPathD(points !== null && points !== void 0 ? points : []);
                        if (!d) {
                            missingHops.push(hop);
                            continue;
                        }
                        resolved.push({ edge: edge, from: hop.from, to: hop.to, pathD: d });
                    }
                }
                catch (e_23_1) { e_23 = { error: e_23_1 }; }
                finally {
                    try {
                        if (run_1_1 && !run_1_1.done && (_b = run_1.return)) _b.call(run_1);
                    }
                    finally { if (e_23) throw e_23.error; }
                }
                if (resolved.length > 0)
                    pathRunsResolved.push(resolved);
            }
        }
        catch (e_22_1) { e_22 = { error: e_22_1 }; }
        finally {
            try {
                if (_u && !_u.done && (_a = _t.return)) _a.call(_t);
            }
            finally { if (e_22) throw e_22.error; }
        }
    }
    // Missing hops: mark error (wins over statusOverrides); pause only when nothing is playable
    var errorOverrides = __assign({}, ((_m = merged.statusOverrides) !== null && _m !== void 0 ? _m : {}));
    if (missingHops.length > 0) {
        try {
            for (var missingHops_1 = __values(missingHops), missingHops_1_1 = missingHops_1.next(); !missingHops_1_1.done; missingHops_1_1 = missingHops_1.next()) {
                var hop = missingHops_1_1.value;
                errorOverrides[hop.from] = 'error';
            }
        }
        catch (e_24_1) { e_24 = { error: e_24_1 }; }
        finally {
            try {
                if (missingHops_1_1 && !missingHops_1_1.done && (_c = missingHops_1.return)) _c.call(missingHops_1);
            }
            finally { if (e_24) throw e_24.error; }
        }
    }
    var declaredPathButEmpty = merged.pathRuns.length > 0 && pathRunsResolved.length === 0;
    if (merged.statusHighlight && (missingHops.length > 0 || declaredPathButEmpty)) {
        var map = createFlowStatusMap();
        var ids = nodeIdsFromHops(merged.pathRuns.flat());
        map.beginRun(ids, merged.pathRuns.flat().map(function (h) { return flowEdgeKey(h.from, h.to); }));
        map.applyOverrides(merged.statusOverrides);
        try {
            for (var missingHops_2 = __values(missingHops), missingHops_2_1 = missingHops_2.next(); !missingHops_2_1.done; missingHops_2_1 = missingHops_2.next()) {
                var hop = missingHops_2_1.value;
                map.setError(hop.from);
            }
        }
        catch (e_25_1) { e_25 = { error: e_25_1 }; }
        finally {
            try {
                if (missingHops_2_1 && !missingHops_2_1.done && (_d = missingHops_2.return)) _d.call(missingHops_2);
            }
            finally { if (e_25) throw e_25.error; }
        }
        paintStaticStatus(map.states, map.edgeStates);
        writeStatusSnapshot(root, map.snapshot(), map.edgeSnapshot());
        if (merged.statusPauseOnError && declaredPathButEmpty) {
            setDiagramState(root, {
                flow: __assign(__assign({}, getDiagramState(root).flow), { paused: true }),
            });
            root.metadata[FLOW_RUNTIME_KEY] = { handles: [], options: merged, playing: false };
            root.markDirty();
            (_o = app.requestRender) === null || _o === void 0 ? void 0 : _o.call(app);
            syncDiagramToolbar(app, root);
            return;
        }
    }
    var statusRuntimeOpts = merged.statusHighlight && edgeLayer
        ? {
            colors: merged.statusColors,
            edges: merged.statusEdges,
            edgeLayer: edgeLayer,
            overrides: Object.keys(errorOverrides).length > 0 ? errorOverrides : undefined,
        }
        : null;
    var markFinished = function () {
        var _a;
        var rt = root.metadata[FLOW_RUNTIME_KEY];
        if (rt)
            rt.playing = false;
        setDiagramState(root, {
            flow: __assign(__assign({}, getDiagramState(root).flow), { paused: true }),
        });
        (_a = app.requestRender) === null || _a === void 0 ? void 0 : _a.call(app);
    };
    if (edgeLayer) {
        try {
            for (var _v = __values(edgeLayer.children), _w = _v.next(); !_w.done; _w = _v.next()) {
                var edgeChild = _w.value;
                var edge = edgeChild;
                var stroke = getEdgeStrokePolyline(edge);
                if (!stroke)
                    continue;
                if (!edgeInPath(edge, hopsSpec))
                    continue;
                if (useDash) {
                    var existing = (_q = (_p = edge.metadata) === null || _p === void 0 ? void 0 : _p.edgeDash) !== null && _q !== void 0 ? _q : (Array.isArray(stroke.dash) ? stroke.dash : undefined);
                    if (!existing || existing.length < 2) {
                        stroke.dash = pattern.slice();
                        edge.metadata.flowDashApplied = true;
                    }
                    stroke.dashOffset = 0;
                    if (dashDur > 0) {
                        handles.push(AnimationEngine.animate(stroke, {
                            dashOffset: period,
                            duration: dashDur,
                            loop: loopDash,
                            easing: 'linear',
                        }));
                    }
                }
            }
        }
        catch (e_26_1) { e_26 = { error: e_26_1 }; }
        finally {
            try {
                if (_w && !_w.done && (_e = _v.return)) _e.call(_v);
            }
            finally { if (e_26) throw e_26.error; }
        }
        if (usePacket) {
            if (merged.pathRuns.length > 0) {
                // Declared path(s): never fall through to ambient all-edge packets
                if (pathRunsResolved.length > 0) {
                    startPathSequence(app, root, pathRunsResolved, merged.speed, merged.highlight, merged.playback, merged.pathGapMs, handles, getOverlay, merged.playback === 'once' ? markFinished : undefined, statusRuntimeOpts);
                }
            }
            else {
                var remaining_1 = 0;
                try {
                    for (var _x = __values(edgeLayer.children), _y = _x.next(); !_y.done; _y = _x.next()) {
                        var edgeChild = _y.value;
                        var edge = edgeChild;
                        if (!edgeMatchesActive(edge, merged.activeEdges))
                            continue;
                        var points = (_r = edge.metadata) === null || _r === void 0 ? void 0 : _r.edgePoints;
                        var d = edgePointsToPathD(points !== null && points !== void 0 ? points : []);
                        if (!d)
                            continue;
                        remaining_1 += 1;
                        startAmbientPacket(app, root, edge, d, merged.speed, merged.highlight, merged.playback, handles, getOverlay, merged.playback === 'once'
                            ? function () {
                                remaining_1 -= 1;
                                if (remaining_1 <= 0)
                                    markFinished();
                            }
                            : undefined);
                    }
                }
                catch (e_27_1) { e_27 = { error: e_27_1 }; }
                finally {
                    try {
                        if (_y && !_y.done && (_f = _x.return)) _f.call(_x);
                    }
                    finally { if (e_27) throw e_27.error; }
                }
            }
        }
    }
    // Static overrides without a path-driven packet run
    if (merged.statusHighlight &&
        merged.statusOverrides &&
        !(pathRunsResolved.length > 0 && usePacket)) {
        var map = createFlowStatusMap();
        map.applyOverrides(merged.statusOverrides);
        paintStaticStatus(map.states);
    }
    // Static activeNodes pulse when not driven by path sequence
    var pathDriven = pathRunsResolved.length > 0 && usePacket;
    if (!pathDriven && (merged.highlight === 'pulse' || merged.highlight === 'breathe')) {
        var active = merged.activeNodes && merged.activeNodes.length > 0
            ? merged.activeNodes
            : inferActiveNodes(root, merged);
        try {
            for (var active_1 = __values(active), active_1_1 = active_1.next(); !active_1_1.done; active_1_1 = active_1.next()) {
                var id = active_1_1.value;
                addNodePulse(app, root, getOverlay(), id, merged.highlight, merged.speed, handles, {
                    loop: merged.playback === 'loop',
                });
            }
        }
        catch (e_28_1) { e_28 = { error: e_28_1 }; }
        finally {
            try {
                if (active_1_1 && !active_1_1.done && (_g = active_1.return)) _g.call(active_1);
            }
            finally { if (e_28) throw e_28.error; }
        }
    }
    root.metadata[FLOW_RUNTIME_KEY] = { handles: handles, options: merged, playing: true };
    root.markDirty();
    (_s = app.requestRender) === null || _s === void 0 ? void 0 : _s.call(app);
    syncDiagramToolbar(app, root);
}
function inferActiveNodes(root, flow) {
    var _a, _b;
    if ((_a = flow.activeNodes) === null || _a === void 0 ? void 0 : _a.length)
        return flow.activeNodes;
    var type = (_b = root.metadata) === null || _b === void 0 ? void 0 : _b.diagramType;
    if (type === 'processPipeline') {
        var ids_2 = [];
        var walk_1 = function (n) {
            var e_29, _a;
            var _b;
            if (((_b = n.metadata) === null || _b === void 0 ? void 0 : _b.pipelineStatus) === 'active') {
                var id = nodeDiagramId(n);
                if (id)
                    ids_2.push(id);
            }
            if ('children' in n) {
                try {
                    for (var _c = __values(n.children), _d = _c.next(); !_d.done; _d = _c.next()) {
                        var c = _d.value;
                        walk_1(c);
                    }
                }
                catch (e_29_1) { e_29 = { error: e_29_1 }; }
                finally {
                    try {
                        if (_d && !_d.done && (_a = _c.return)) _a.call(_c);
                    }
                    finally { if (e_29) throw e_29.error; }
                }
            }
        };
        walk_1(root);
        return ids_2;
    }
    return [];
}
/** Re-apply flow from `diagramState.flow` after reroute / rebuild. */
export function refreshDiagramFlow(app, root) {
    var flow = getDiagramState(root).flow;
    if (!flow || flow.enabled === false)
        return;
    applyDiagramFlow(app, root, flow);
}
/** If builder options include `flow`, start animation and persist on the group. */
export function maybeApplyDiagramFlow(app, root, options) {
    var flow = options.flow;
    if (!flow || typeof flow !== 'object')
        return;
    applyDiagramFlow(app, root, flow);
}
/** Pause flow in place (packet / dashes / status keep position; resume continues). */
export function pauseDiagramFlow(app, root) {
    var e_30, _a;
    var _b, _c, _d, _e;
    var prev = (_b = getDiagramState(root).flow) !== null && _b !== void 0 ? _b : {};
    if (prev.enabled === false)
        return;
    var rt = (_c = root.metadata) === null || _c === void 0 ? void 0 : _c[FLOW_RUNTIME_KEY];
    if ((rt === null || rt === void 0 ? void 0 : rt.playing) && rt.handles.length > 0) {
        try {
            for (var _f = __values(rt.handles), _g = _f.next(); !_g.done; _g = _f.next()) {
                var h = _g.value;
                try {
                    (_d = h.pause) === null || _d === void 0 ? void 0 : _d.call(h);
                }
                catch (_h) {
                    /* ignore */
                }
            }
        }
        catch (e_30_1) { e_30 = { error: e_30_1 }; }
        finally {
            try {
                if (_g && !_g.done && (_a = _f.return)) _a.call(_f);
            }
            finally { if (e_30) throw e_30.error; }
        }
        rt.playing = false;
        rt.softPaused = true;
        setDiagramState(root, {
            flow: __assign(__assign({}, prev), { paused: true }),
        });
        root.markDirty();
        (_e = app.requestRender) === null || _e === void 0 ? void 0 : _e.call(app);
        syncDiagramToolbar(app, root);
        return;
    }
    // Fallback: no live runtime (already stopped) — persist paused flag + static chrome
    applyDiagramFlow(app, root, __assign(__assign({}, prev), { paused: true }));
}
/** Resume flow from the paused step (soft pause) or restart if no live runtime. */
export function resumeDiagramFlow(app, root) {
    var e_31, _a;
    var _b, _c, _d, _e;
    var prev = (_b = getDiagramState(root).flow) !== null && _b !== void 0 ? _b : {};
    if (prev.enabled === false)
        return;
    var speed = typeof prev.speed === 'number' && prev.speed > 0 ? prev.speed : 1;
    var rt = (_c = root.metadata) === null || _c === void 0 ? void 0 : _c[FLOW_RUNTIME_KEY];
    if ((rt === null || rt === void 0 ? void 0 : rt.softPaused) && rt.handles.length > 0) {
        try {
            for (var _f = __values(rt.handles), _g = _f.next(); !_g.done; _g = _f.next()) {
                var h = _g.value;
                try {
                    (_d = h.resume) === null || _d === void 0 ? void 0 : _d.call(h);
                }
                catch (_h) {
                    /* ignore */
                }
            }
        }
        catch (e_31_1) { e_31 = { error: e_31_1 }; }
        finally {
            try {
                if (_g && !_g.done && (_a = _f.return)) _a.call(_f);
            }
            finally { if (e_31) throw e_31.error; }
        }
        rt.playing = true;
        rt.softPaused = false;
        setDiagramState(root, {
            flow: __assign(__assign({}, prev), { paused: false, speed: speed }),
        });
        root.markDirty();
        (_e = app.requestRender) === null || _e === void 0 ? void 0 : _e.call(app);
        syncDiagramToolbar(app, root);
        return;
    }
    // Fallback: rebuild from the start (e.g. after once finished / hard stop)
    applyDiagramFlow(app, root, __assign(__assign({}, prev), { paused: false, speed: speed }));
}
/** Toggle pause / resume. Returns whether flow is playing after the call. */
export function toggleDiagramFlowPause(app, root) {
    var _a, _b, _c;
    var prev = (_a = getDiagramState(root).flow) !== null && _a !== void 0 ? _a : {};
    var rt = (_b = root.metadata) === null || _b === void 0 ? void 0 : _b[FLOW_RUNTIME_KEY];
    var playing = (rt === null || rt === void 0 ? void 0 : rt.playing) === true && prev.paused !== true && ((_c = prev.speed) !== null && _c !== void 0 ? _c : 1) > 0;
    if (playing) {
        pauseDiagramFlow(app, root);
        return false;
    }
    resumeDiagramFlow(app, root);
    return true;
}
/** Replay from the start (clears pause; useful after `playback: 'once'`). */
export function replayDiagramFlow(app, root) {
    var _a;
    var prev = (_a = getDiagramState(root).flow) !== null && _a !== void 0 ? _a : {};
    if (prev.enabled === false)
        return;
    var speed = typeof prev.speed === 'number' && prev.speed > 0 ? prev.speed : 1;
    applyDiagramFlow(app, root, __assign(__assign({}, prev), { paused: false, speed: speed, enabled: true }));
}
/** Whether flow is currently animating. */
export function isDiagramFlowPlaying(root) {
    var _a, _b;
    var flow = getDiagramState(root).flow;
    if (!flow || flow.enabled === false || flow.paused || ((_a = flow.speed) !== null && _a !== void 0 ? _a : 1) <= 0)
        return false;
    var rt = (_b = root.metadata) === null || _b === void 0 ? void 0 : _b[FLOW_RUNTIME_KEY];
    return (rt === null || rt === void 0 ? void 0 : rt.playing) === true;
}
